import * as pc from "playcanvas";

import type { Number16 } from "../types";
import type { ReadonlyQuaternion } from "./Quaternion";
import { type ReadonlyVector3, Vector3 } from "./Vector3";
import type { ReadonlyVector4, Vector4 } from "./Vector4";

export interface ReadonlyMatrix4 {
  get data(): Float32Array;

  scaleSign: number;

  clone(): Matrix4;
  equals(rhs: ReadonlyMatrix4): boolean;
  getEulerAngles(eulers?: Vector3): Vector3;
  getScale(scale?: Vector3): Vector3;
  getTranslation(t?: Vector3): Vector3;
  getX(x?: Vector3): Vector3;
  getY(y?: Vector3): Vector3;
  getZ(z?: Vector3): Vector3;
  isIdentity(): boolean;
  toString(): string;
  transformPoint(vec: ReadonlyVector3, res?: Vector3): Vector3;
  transformVec4(vec: ReadonlyVector4, res?: Vector4): Vector4;
  transformVector(vec: ReadonlyVector3, res?: Vector3): Vector3;
  toArray(): Number16;
  copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;

  // Extensions
  getTRS(translation?: Vector3, rotation?: Vector3, scale?: Vector3): [Vector3, Vector3, Vector3];
  toJSON(): Number16;

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Matrix4;
}

declare module "playcanvas" {
  interface Mat4 extends ReadonlyMatrix4 {
    data: Float32Array;
    scaleSign: number;

    add(rhs: ReadonlyMatrix4): this;
    add2(lhs: ReadonlyMatrix4, rhs: ReadonlyMatrix4): this;
    clone(): Matrix4;
    copy(rhs: ReadonlyMatrix4): this;
    equals(rhs: ReadonlyMatrix4): boolean;
    getEulerAngles(eulers?: Vector3): Vector3;
    getScale(scale?: Vector3): Vector3;
    getTranslation(t?: Vector3): Vector3;
    getX(x?: Vector3): Vector3;
    getY(y?: Vector3): Vector3;
    getZ(z?: Vector3): Vector3;
    invert(src?: ReadonlyMatrix4): this;
    isIdentity(): boolean;
    mul(rhs: ReadonlyMatrix4): this;
    mul2(lhs: ReadonlyMatrix4, rhs: ReadonlyMatrix4): this;
    mulAffine2(lhs: ReadonlyMatrix4, rhs: ReadonlyMatrix4): this;
    set(src: Readonly<ArrayLike<number>>): this;
    setFrustum(left: number, right: number, bottom: number, top: number, znear: number, zfar: number): this;
    setFromAxisAngle(axis: ReadonlyVector3, angle: number): this;
    setFromEulerAngles(ex: number, ey: number, ez: number): this;
    setIdentity(): this;
    setLookAt(position: ReadonlyVector3, target: ReadonlyVector3, up: ReadonlyVector3): this;
    setOrtho(left: number, right: number, bottom: number, top: number, near: number, far: number): this;
    setPerspective(fov: number, aspect: number, znear: number, zfar: number, fovIsHorizontal?: boolean): this;
    setReflection(normal: ReadonlyVector3, distance: number): this;
    setScale(x: number, y: number, z: number): this;
    setTRS(t: ReadonlyVector3, r: ReadonlyQuaternion, s: ReadonlyVector3): this;
    setTranslate(x: number, y: number, z: number): this;
    setViewport(x: number, y: number, width: number, height: number): this;
    toString(): string;
    transformPoint(vec: ReadonlyVector3, res?: Vector3): Vector3;
    transformVec4(vec: ReadonlyVector4, res?: Vector4): Vector4;
    transformVector(vec: ReadonlyVector3, res?: Vector3): Vector3;
    transpose(src?: ReadonlyMatrix4): this;
    toArray(): Number16;
    copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;

    // Extensions
    fromArray(array: Number16, offset?: number | undefined): Matrix4;
    getTRS(translation?: Vector3, rotation?: Vector3, scale?: Vector3): [Vector3, Vector3, Vector3];
    recycle(): void;
    toJSON(): Number16;
  }
}

pc.Mat4.prototype.fromArray = function (array: Number16, offset?: number | undefined) {
  this.data.set(array, offset);
  return this;
};

pc.Mat4.prototype.getTRS = function (
  translation = Vector3.makeUninitialized(),
  rotation = Vector3.makeUninitialized(),
  scale = Vector3.makeUninitialized()
) {
  this.getTranslation(translation);
  this.getEulerAngles(rotation);
  this.getScale(scale);
  return [translation, rotation, scale];
};

pc.Mat4.prototype.recycle = function () {
  pool.push(this);
};

pc.Mat4.prototype.toJSON = function () {
  const m = this.data;
  return [m[0], m[1], m[2], m[3], m[4], m[5], m[6], m[7], m[8], m[9], m[10], m[11], m[12], m[13], m[14], m[15]];
};

pc.Mat4.prototype.unsafeCast = function () {
  return this;
};

const pool: Matrix4[] = [];

// @ts-expect-error - Override static constants type (intentionally incompatible with base class)
export class Matrix4 extends pc.Mat4 {
  static override IDENTITY = Matrix4.make().setIdentity() as ReadonlyMatrix4;

  /**
   * @summary Creates a new matrix with identity values.
   */
  static make(data?: Readonly<Float32Array>): Matrix4 {
    const m = pool.pop() ?? new Matrix4();

    if (data) {
      if (data.length !== 16) {
        throw new Error("Matrix4 data must be a Float32Array of length 16");
      }

      m.set(data);
    } else {
      m.setIdentity();
    }

    return m;
  }

  /**
   * @summary Creates a new readonly matrix with identity values.
   */
  static makeReadonly(): ReadonlyMatrix4 {
    const m = pool.pop() ?? new Matrix4();
    m.setIdentity();
    Object.freeze(m);
    return m;
  }

  /**
   * @summary Creates a new uninitialized matrix.
   */
  static makeUninitialized(): Matrix4 {
    return pool.pop() || new Matrix4();
  }
}
